'use client'

import { useParams } from 'next/navigation'
import { useCallback, useRef } from 'react'
import { getClassProject } from './classProject'

declare global {
  interface Window {
    gtag?: (...args: (string | object)[]) => void
  }
}

type EventParameters = Record<
  string,
  string | number | boolean | undefined
> | null

// Custom tracking so we can attach event parameters GA's automatic tracking can't.
export function useTracking() {
  // Params could be null in Storybook
  const pageParams: Record<string, string> | null =
    useParams<Record<string, string>>()
  const lastCall = useRef(0)

  const paramsToSnakeCase = useCallback((params: EventParameters = {}) => {
    if (!params) {
      return {}
    }
    return Object.keys(params).reduce((acc, key) => {
      const snakeCaseKey = key
        .split(/\.?(?=[A-Z])/)
        .join('_')
        .toLowerCase()
      return {
        ...acc,
        ...(key in params ? { [snakeCaseKey]: params[key] } : {}),
      }
    }, {})
  }, [])

  const trackEvent = (name: string, params?: EventParameters) => {
    const payload = {
      ...pageParams,
      ...getClassProject(),
      ...params,
    }
    if (Date.now() - lastCall.current < 1000) {
      // Throttle to prevent over-reporting (as the official GA library does).
      return
    }

    lastCall.current = Date.now()
    window.gtag?.('event', name, paramsToSnakeCase(payload))
  }

  /* eslint-disable no-redeclare -- allow method overloading */
  function track(
    event: 'video_start',
    params: {
      videoProvider: string
      videoUrl: string
      videoTitle: string
    }
  ): void
  function track(
    event: 'video_progress',
    params: {
      videoPercent: number
      videoProvider: string
      videoUrl: string
      videoTitle: string
    }
  ): void
  function track(
    event: 'video_progress_time',
    params: {
      videoSeconds: number
      videoProvider: string
      videoUrl: string
      videoTitle: string
    }
  ): void
  function track(
    event: 'video_complete',
    params: {
      videoProvider: string
      videoUrl: string
      videoTitle: string
    }
  ): void
  function track(
    event: 'view_search_results',
    params: { searchTerm: string }
  ): void
  function track(
    event: 'button_click',
    params: { buttonName: string; context?: string } & Record<
      string,
      string | boolean
    >
  ): void
  function track(event: 'on_hover', params: { element: string }): void
  function track(event: 'modal_view', params: { modalName: string }): void
  function track(
    event: 'view_resource',
    params: {
      resourceName: string
      resourceId: number
      resourceType: string
    } & Record<string, string | number | boolean>
  ): void
  function track(
    event: 'file_download',
    params: {
      fileExtension: 'pdf' | 'docx' | 'pptx' | 'xlsx'
      fileName: string
      linkText: string
      linkUrl: string
    } & Record<string, string>
  ): void
  function track(event: 'page_view'): void
  function track(
    event: 'print_lesson',
    params: { lessonOrder: number; publishedProjectId: number }
  ): void
  function track(
    event:
      | 'file_download'
      | 'button_click'
      | 'on_hover'
      | 'modal_view'
      | 'view_resource'
      | 'video_progress_time'
      | 'page_view'
      | 'view_search_results'
      | 'video_start'
      | 'video_progress'
      | 'video_complete'
      | 'print_lesson',
    params?: Record<string, string | number | boolean>
  ): void {
    trackEvent(event, params ?? {})
  }
  /* eslint-enable no-redeclare -- disallow */
  return {
    track,
  }
}
